import { useState } from "react";
import { Link } from "react-router-dom";

/**
 * Missed-call cost calculator: the conversion centerpiece of the Frayze Reception
 * page. Same leak math as the homepage ROI calculator (missed/wk x 4.3 x value x
 * close%), framed on CALLS, with the Reception monthly cost as the comparison line.
 */
export const MissedCallCalculator = ({
  monthlyPrice = 197,
  ctaLink = "/book-consultation/?tier=intelligence",
}: {
  monthlyPrice?: number;
  ctaLink?: string;
}) => {
  const [missed, setMissed] = useState(8);
  const [value, setValue] = useState(2500);
  const [close, setClose] = useState(30);

  const monthlyLeak = Math.round(missed * 4.3 * value * (close / 100));
  const fmt = (n: number) => "$" + n.toLocaleString("en-CA");

  const slider = (
    label: string,
    display: string,
    val: number,
    set: (n: number) => void,
    min: number,
    max: number,
    step: number
  ) => (
    <label className="flex flex-col gap-3">
      <span className="flex justify-between text-sm md:text-base font-semibold text-[#5A6270]">
        {label}
        <strong className="text-[#101319] font-bold text-base md:text-lg">{display}</strong>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={val}
        onChange={(e) => set(Number(e.target.value))}
        className="w-full h-1.5 bg-[#E9E6DE] rounded-lg appearance-none cursor-pointer accent-purple-700"
      />
    </label>
  );

  return (
    <section className="max-w-[1312px] mx-auto px-6">
      <div className="flex flex-col gap-4 max-w-2xl text-left mb-10">
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#101319]">
          What are missed calls costing you?
        </h2>
        <p className="text-base md:text-lg leading-relaxed text-[#5A6270]">
          Most callers who hit voicemail never call back. Put in your numbers and see
          what walks out the door every month your phone goes unanswered.
        </p>
      </div>

      <div className="bg-white border border-[#E9E6DE] rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-2 shadow-lg select-none">
        <div className="p-8 md:p-12 flex flex-col gap-8 text-left justify-center">
          {slider("Missed calls per week", String(missed), missed, setMissed, 1, 40, 1)}
          {slider("Average job / deal value", fmt(value), value, setValue, 100, 20000, 100)}
          {slider("Close rate on calls you answer", close + "%", close, setClose, 5, 80, 5)}
          <div className="text-xs text-[#5A6270] font-medium">
            Estimate only, based on calls you already get but don't answer.
          </div>
        </div>

        <div className="bg-[#101319] text-[#F2F3F5] p-8 md:p-12 flex flex-col justify-center gap-5 text-left">
          <div className="text-xs tracking-[0.16em] uppercase text-[#9BA3B2]">
            Walking out the door
          </div>
          <div className="text-4xl sm:text-5xl md:text-[56px] font-bold tracking-tight text-white leading-none">
            {fmt(monthlyLeak)}
          </div>
          <div className="text-base sm:text-lg text-[#9BA3B2]">
            in winnable revenue per month. That's <span className="text-white font-bold">{fmt(monthlyLeak * 12)} a year</span>.
          </div>
          <div className="text-sm text-[#9BA3B2] border-t border-white/10 pt-4">
            Frayze Reception answers every one of those calls for{" "}
            <span className="text-white font-bold">${monthlyPrice}/month</span>
            {monthlyLeak > monthlyPrice ? (
              <>, about {Math.max(1, Math.round(monthlyLeak / monthlyPrice))}x less than what the missed calls cost.</>
            ) : (
              <>.</>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 mt-2">
            <Link
              to={ctaLink}
              className="text-center bg-purple-700 hover:bg-purple-800 text-white font-bold px-6 py-3.5 rounded-xl shadow-md transition-all hover:scale-[1.02]"
            >
              Hear It in Action
            </Link>
            <Link
              to="/book-consultation/"
              className="text-center border border-white/20 hover:border-white/50 text-[#F2F3F5] font-bold px-6 py-3.5 rounded-xl transition-all hover:scale-[1.02]"
            >
              Book a 15-min call
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
